import React, { useEffect, useState } from "react";
import { pxToRem } from "@/util/common";
import { createUseStyles } from "react-jss";
import { InfiniteScroll, PullToRefresh, Image, Grid } from "antd-mobile";
import InfiniteScrollContent from "@/components/InfiniteScrollContent";
import API from "@/api";
import { showGlobalLoading } from "@/components/globalLoading";
import useCustomTranslation from "@/hooks/useTranslation";
import CustomEmpty from "@/components/Empty";
import { gameColumns, GameItem } from "../type";
import { isEmpty } from "lodash";
import i18n, { translateLanguage } from "@/locales";

const GameItemNode: React.FC<{
	imageSrc: string;
	name: string;
	play: string;
}> = ({ imageSrc, name, play }) => {
	const classes = useStyles();
	return (
		<div className={classes.gameSingle}>
			<Image className="img" src={imageSrc} fit="cover" />
			<div className="cont">
				<div className="name">{name}</div>
				<div className="play">{play}</div>
			</div>
		</div>
	);
};

const EvoList: React.FC<{
	gameCompany: string;
	onBack: () => void;
	onClick: (game: GameItem) => void;
}> = ({ gameCompany, onBack, onClick }) => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const { showLoading, hideLoading } = showGlobalLoading();
	const [data, setData] = useState<gameColumns | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const [page, setPage] = useState(0);
	const pageSize = 15;
	const fetchListHandle = async (isRefresh = false) => {
		try {
			showLoading();
			let pageNum = isRefresh ? 0 : page;
			const params: any = {
				page: pageNum,
				pageSize: pageSize,
				gameCompany,
			};
			const result: any = await API.games.apiGetGames(params);
			if (result?.code === 0) {
				const contentData = result.content.data?.gameColumns[0];
				const haveMore = result.content.haveMore;
				setHasMore(haveMore);
				if (haveMore) {
					if (isRefresh) {
						setPage(1);
					} else {
						setPage((prevPage) => prevPage + 1);
					}
				}
				if (pageNum === 0) {
					setData(contentData);
				} else {
					setData((prevData) => {
						if (prevData === null) {
							return {
								gameVOList: contentData.gameVOList,
								gameCompany: contentData.gameCompany,
								categoryDesc: contentData.categoryDesc,
								logo: contentData.logo,
								categoryOtherDesc:
									contentData.categoryOtherDesc,
							};
						}
						return {
							...prevData,
							gameVOList: [
								...(prevData.gameVOList || []),
								...contentData.gameVOList,
							],
						};
					});
				}
			}
		} catch (error) {
			console.log("---postUserAgentManagerList--:", error);
		} finally {
			hideLoading();
		}
	};
	const handleRefresh = async () => {
		// 模拟刷新数据
		await fetchListHandle(true);
	};

	const handleFetchMore = async () => {
		// 模拟加载更多数据
		await fetchListHandle();
	};

	useEffect(() => {
		fetchListHandle();
	}, []);

	return (
		<div className={`router-view ${classes.evoListWrap}`}>
			<PullToRefresh
				onRefresh={handleRefresh}
				canReleaseText={t("canReleaseText")}
				completeText={t("completeText")}
				pullingText={t("pullingText")}
				refreshingText={t("refreshingText")}
			>
				<div className={classes.columnTitle}>
					<div onClick={onBack} className="l">
						<Image
							className="i"
							src={data?.logo}
							fit="cover"
							placeholder={
								<Image
									className="i"
									src={require("@/assets/images/sideBar/liveCasino.png")}
									fit="cover"
								/>
							}
						/>
						{!isEmpty(data) && (
							<strong>
								{translateLanguage(
									data?.categoryOtherDesc ?? "",
									i18n.language
								)}
							</strong>
						)}
					</div>
				</div>
				{!isEmpty(data) ? (
					<Grid columns={3} gap={[16, 14]}>
						{data.gameVOList.map((game, index) => (
							<Grid.Item
								key={index}
								onClick={() => onClick(game)}
							>
								<GameItemNode
									imageSrc={game.coverPic}
									name={game.name}
									play={game.gameCompany}
								/>
							</Grid.Item>
						))}
					</Grid>
				) : (
					<CustomEmpty />
				)}

				{!isEmpty(data) && data.gameVOList?.length > 0 && (
					<InfiniteScroll
						loadMore={handleFetchMore}
						hasMore={hasMore}
					>
						<InfiniteScrollContent hasMore={hasMore} />
					</InfiniteScroll>
				)}
			</PullToRefresh>
		</div>
	);
};

export default EvoList;

const useStyles = createUseStyles({
	evoListWrap: {
		padding: {
			left: pxToRem(20),
			right: pxToRem(20),
		},
	},
	columnTitle: {
		display: "flex",
		justifyContent: "space-between",
		padding: {
			right: pxToRem(20),
			top: pxToRem(12),
			bottom: pxToRem(12),
		},
		"& .l": {
			flex: 1,
			overflow: "hidden",
			display: "flex",
			alignItems: "center",
			"& .i": {
				width: pxToRem(24),
				height: pxToRem(24),
				marginRight: pxToRem(8),
			},
			"& strong": {
				color: "#FFFFFF",
				fontFamily: "Noto Sans SC",
				fontSize: pxToRem(14),
				fontWeight: 700,
			},
		},
	},
	gameSingle: {
		width: pxToRem(100),
		"& .img": {
			height: pxToRem(120),
			borderRadius: pxToRem(14),
			border: "1px solid #384F7A",
		},
		"& .cont": {
			paddingTop: pxToRem(6),
		},
		"& .name": {
			color: "#FFFFFF",
			fontSize: pxToRem(14),
			fontWeight: 500,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
		"& .play": {
			color: "#868FA8",
			fontSize: pxToRem(12),
			fontWeight: 400,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
		},
	},
});
